/**
 * edgLayout — dagre auto-layout for the React Flow EDG (phase 6c, step 2).
 *
 * Port of the old EDG auto-layout (`components/EDG/index.js:autoLayout` +
 * `lib/joint/layout.js`), which called `joint.layout.DirectedGraph.layout(graph,
 * { nodeSep: 50, edgeSep: 80, rankDir })`. JointJS's `DirectedGraph` is a thin
 * wrapper over `dagre`, so this module drives `@dagrejs/dagre` directly with the
 * SAME constants and reproduces the SAME position semantics:
 *
 *   - dagre lays out every node + edge, then returns each node's CENTER position.
 *   - JointJS converts that center to the element's TOP-LEFT position via
 *     `x - width/2`, `y - height/2` before calling `element.set('position', ...)`.
 *     We do the identical conversion so the resulting MST positions match.
 *   - `rankSep` was never passed by the old code, so dagre's default (50) applied;
 *     we set it explicitly for clarity.
 *
 * The old layout ran on the JointJS graph (whose cells mirrored the metamodel +
 * uimodel), then each cell's `change:position` flowed back to `updateEDGPosition`
 * — one MST action (and one undo entry) per moved node. Here we compute the
 * positions purely in JS from the metamodel topology + uimodel node sizes,
 * returning a plain list the caller commits through the existing
 * `updateEDGPosition` command per node (so undo/redo + autosave work for free,
 * with the SAME per-node history granularity the old EDG had). The caller also
 * clears all vertices first, exactly like the old `autoLayout` (the layout
 * algorithm does not move vertices, so they would otherwise dangle).
 *
 * No JointJS / Rappid import — standalone, matching the rest of `src/diagram/`.
 */
import dagre from '@dagrejs/dagre';

// The old EDG auto-layout constants (components/EDG/index.js:autoLayout).
export const NODE_SEP = 50;
export const EDGE_SEP = 80;
// dagre's default rankSep (JointJS DirectedGraph used it because `rankSep` was
// never passed). Set explicitly so the spacing is pinned, not implicit.
export const RANK_SEP = 50;

/** The two layout directions the old EDG exposed (BUL button → BT, TDL → TD). */
export type RankDir = 'BT' | 'TD';

export type LayoutNodeInput = {
  /** Object type id (the React Flow node id + the dagre node id). */
  id: string;
  /** Node width in px (from the uimodel node size). */
  width: number;
  /** Node height in px (from the uimodel node size). */
  height: number;
};

export type LayoutEdgeInput = {
  /** Source object type id. */
  source: string;
  /** Target object type id. */
  target: string;
};

export type LayoutPosition = {
  /** Object type id. */
  id: string;
  /** New TOP-LEFT x (dagre centre − width/2), rounded to integer like positions. */
  x: number;
  /** New TOP-LEFT y (dagre centre − height/2). */
  y: number;
};

/**
 * Run a dagre layout over the EDG topology and return the new TOP-LEFT positions
 * for every node. Pure: no MST mutation, no React Flow dependency. The caller
 * commits the returned positions through `updateEDGPosition`.
 *
 * `rankDir` maps the old EDG buttons: 'BT' = Auto-layout Bottom Up (layout_bul),
 * 'TD' = Auto-layout Top Down (layout_tdl).
 */
export function computeEdgLayout(
  nodes: LayoutNodeInput[],
  edges: LayoutEdgeInput[],
  rankDir: RankDir
): LayoutPosition[] {
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: rankDir,
    nodesep: NODE_SEP,
    edgesep: EDGE_SEP,
    ranksep: RANK_SEP,
  });
  // dagre requires a default edge label factory.
  g.setDefaultEdgeLabel(() => ({}));

  for (const node of nodes) {
    g.setNode(node.id, { width: node.width, height: node.height });
  }
  for (const edge of edges) {
    // Only wire edges whose endpoints are both laid-out nodes (defensive — the
    // metamodel should never reference a missing object type, but a half-built
    // model during editing could).
    if (g.hasNode(edge.source) && g.hasNode(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  }

  dagre.layout(g);

  const result: LayoutPosition[] = [];
  for (const node of nodes) {
    const laidOut = g.node(node.id);
    if (!laidOut) continue;
    // dagre returns the node CENTER; convert to TOP-LEFT exactly like JointJS
    // DirectedGraph did, and round to integers to match the position command
    // (positions are Math.round'd everywhere in the EDG).
    result.push({
      id: node.id,
      x: Math.round(laidOut.x - node.width / 2),
      y: Math.round(laidOut.y - node.height / 2),
    });
  }
  return result;
}
